import {
  Accordion,
  Button,
  Divider,
  FixedFooter,
  Input,
  InputNumber,
} from "@components/";
import { useNewProduct } from "./useNewProduct";
import { FaCheck } from "react-icons/fa6";
import { useRef, useEffect } from "react";
import { ProductSearch } from "../NewOrder/ProductSearch";
import type { ProductResult } from "../NewOrder/interfaces";

export function NewProduct() {
  // Prevenir smooth scrolling durante toda a sessão
  useEffect(() => {
    const originalScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';
    
    return () => {
      document.documentElement.style.scrollBehavior = originalScrollBehavior;
      document.body.style.scrollBehavior = '';
    };
  }, []);

  const pageRef = useRef<HTMLDivElement>(null);
  const { 
    step, 
    setStep,
    isLoading,
    selectedProduct,
    setSelectedProduct,
    quantidade,
    setQuantidade,
    preco,
    setPreco,
    addProductToSideConversation,
    handleBackToOrder,
    query,
    results,
    open,
    searchLoading,
    error,
    highlight,
    clearQuery,
    handleProductSelect
  } = useNewProduct();

  if (step === 3) {
    return (
      <div className="h-full mb-4 flex flex-col gap-[6.5px] py-[22.5px] px-4">
        <h1 className="text-[18px] font-semibold">
          Pedidos <span className="text-[#87929D]">• Adicionar produto</span>
        </h1>
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="w-11 h-11 rounded-full border-2 flex items-center justify-center border-[#107048]">
            <FaCheck size={25} className="text-xs text-[#107048]" />
          </div>
          <h2 className="text-[22px] font-semibold text-[#107048]">
            Produto adicionado com sucesso!
          </h2>
        </div>

        <div className="mt-8">
          {selectedProduct && (
            <Accordion
              title={selectedProduct.name}
              fields={[
                "Nome",
                "Código do produto",
                "Marca",
                "Linha",
                "Família do produto",
                "Código ERP",
                "Ativo",
                "Peso",
                "Altura",
                "Largura",
                "Profundidade",
                "Quantidade",
                "Preço"
              ]}
              values={[
                selectedProduct.name,
                selectedProduct.codigo,
                selectedProduct.marca,
                selectedProduct.linha,
                selectedProduct.familiaProduto,
                selectedProduct.codigoErp,
                selectedProduct.ativo ? "Sim" : "Não",
                selectedProduct.peso,
                selectedProduct.altura,
                selectedProduct.largura,
                selectedProduct.profundidade,
                quantidade,
                preco
              ]}
              defaultOpen={true}
            />
          )}
        </div>

        <FixedFooter pageRef={pageRef}>
          <Button variant="outline" onClick={handleBackToOrder}>
            Voltar para pedido
          </Button>
        </FixedFooter>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="flex flex-col gap-[6.5px] py-[22.5px] px-4">
      <h1 className="text-[18px] font-semibold">
        Pedidos <span className="text-[#87929D]">• Adicionar produto</span>
      </h1>

      {step === 1 && (
        <form className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col gap-2">
            <div className="relative">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <ProductSearch
                    onSelect={(product: ProductResult) => {
                      // Capturar posição do scroll antes de adicionar o produto
                      const currentScrollTop =
                        window.pageYOffset || document.documentElement.scrollTop;
                        
                      console.log(
                        "🔧 DEBUG: ProductSearch onSelect chamado com:",
                        product
                      );
                      console.log(
                        "🔧 DEBUG: Scroll atual antes de adicionar:",
                        currentScrollTop
                      );
        
                      // Adicionar o produto
                      //addProduct(product, 1, "");
                      handleProductSelect(product);
        
                      // Usar múltiplas tentativas para restaurar o scroll
                      // pois o re-render pode demorar um pouco mais
                      const restoreScroll = () => {
                        window.scrollTo({
                          top: currentScrollTop,
                          behavior: "auto",
                        });
                      };
        
                      // Tentar restaurar várias vezes com delays diferentes
                      setTimeout(restoreScroll, 50);
                      setTimeout(restoreScroll, 100);
                      setTimeout(restoreScroll, 200);
                      setTimeout(restoreScroll, 300);
        
                      console.log("✅ DEBUG: Scroll restoration scheduled");
                    }}
                    placeholder="Busque por nome, código ou SKU"
                  />

                  {query && (
                    <button
                      type="button"
                      onClick={clearQuery}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {searchLoading && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-2">
                  <div className="text-sm text-gray-500">Buscando produtos...</div>
                </div>
              )}

              {error && open && (
                <div className="absolute top-full left-0 right-0 bg-white border border-red-200 rounded-md shadow-lg z-10 p-2">
                  <div className="text-sm text-red-600">{error}</div>
                </div>
              )}

              {open && !searchLoading && !error && results.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                  {results.map((item, index) => {
                    return (
                      <div
                        key={item.id}
                        className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                          index === highlight ? "bg-blue-50" : "hover:bg-gray-50"
                        }`}
                        onClick={(e) => {
                          e.preventDefault(); // Prevenir comportamento padrão
                          e.stopPropagation(); // Parar propagação do evento
                          console.log("🔧 DEBUG: Produto clicado no NewProduct JSX:", item);
                          console.log("🔧 DEBUG: Tipo do produto:", typeof item);
                          handleProductSelect(item);
                        }}
                      >
                        <div className="font-medium text-sm">{item.name}</div>
                        {item.custom_object_fields?.codigo_do_produto != null && (
                          <div className="text-xs text-gray-500 mt-1">
                            Código:{" "}
                            {String(item.custom_object_fields.codigo_do_produto)}
                          </div>
                        )}
                        {item.custom_object_fields?.descricao_do_produto != null && (
                          <div className="text-xs text-gray-500 mt-1">
                            {String(item.custom_object_fields.descricao_do_produto)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {open &&
                !searchLoading &&
                !error &&
                results.length === 0 &&
                query.length >= 2 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-2">
                    <div className="text-sm text-gray-500">
                      Nenhum produto encontrado
                    </div>
                  </div>
                )}
            </div>
          </div>

          {selectedProduct && (
            <>
              <Divider />

              <div className="flex flex-col py-2 px-4 border border-[#D8DCDE] rounded gap-4">
                <h4 className="font-semibold text-sm">{selectedProduct.name}</h4>
                <Input 
                  label="Nome" 
                  value={selectedProduct.name} 
                  disabled 
                />
                <div className="grid grid-cols-2 gap-2 w-full">
                  <InputNumber 
                    fullSize 
                    label="Quantidade" 
                    value={parseInt(quantidade)} 
                    min={1}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      setQuantidade(value.toString());
                    }}
                    onIncrement={(value) => {
                      // Capturar posição do scroll antes da atualização
                      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                      
                      setQuantidade(value.toString());
                      
                      // Restaurar posição com múltiplas tentativas
                      const restoreScroll = () => {
                        window.scrollTo({
                          top: currentScrollTop,
                          behavior: 'auto'
                        });
                      };
                      
                      setTimeout(restoreScroll, 10);
                      setTimeout(restoreScroll, 50);
                      setTimeout(restoreScroll, 100);
                    }}
                    onDecrement={(value) => {
                      // Capturar posição do scroll antes da atualização
                      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                      
                      setQuantidade(value.toString());
                      
                      // Restaurar posição com múltiplas tentativas
                      const restoreScroll = () => {
                        window.scrollTo({
                          top: currentScrollTop,
                          behavior: 'auto'
                        });
                      };
                      
                      setTimeout(restoreScroll, 10);
                      setTimeout(restoreScroll, 50);
                      setTimeout(restoreScroll, 100);
                    }}
                  />
                  <Input
                    label="Valor Uni."
                    value={preco}
                    placeholder="Ex.: R$ 100.00"
                    required={false}
                    onChange={(e) => setPreco(e.target.value)}
                  />
                </div>

                <div>
                  <Button 
                    variant="outline-danger" 
                    onClick={() => setSelectedProduct(null)}
                  >
                    Remover
                  </Button>
                </div>
              </div>
            </>
          )}

          <FixedFooter pageRef={pageRef}>
            <Button 
              onClick={() => setStep(2)}
              disabled={!selectedProduct || !quantidade}
            >
              Prosseguir
            </Button>
            <Button variant="outline" onClick={handleBackToOrder}>
              Voltar
            </Button>
          </FixedFooter>
        </form>
      )}

      {step === 2 && selectedProduct && (
        <>
          <h4 className="text-sm font-semibold">Resumo do Produto</h4>
          <Accordion
            title={selectedProduct.name}
            fields={[
              "Nome",
              "Código do produto",
              "Marca",
              "Linha",
              "Família do produto",
              "Código ERP",
              "Ativo",
              "Peso",
              "Altura",
              "Largura",
              "Profundidade",
              "Quantidade",
              "Preço"
            ]}
            values={[
              selectedProduct.name,
              selectedProduct.codigo,
              selectedProduct.marca,
              selectedProduct.linha,
              selectedProduct.familiaProduto,
              selectedProduct.codigoErp,
              selectedProduct.ativo ? "Sim" : "Não",
              selectedProduct.peso,
              selectedProduct.altura,
              selectedProduct.largura,
              selectedProduct.profundidade,
              quantidade,
              preco
            ]}
            defaultOpen={true}
          />

          <FixedFooter pageRef={pageRef}>
            <Button 
              onClick={addProductToSideConversation}
              disabled={isLoading}
            >
              {isLoading ? "Adicionando..." : "Adicionar produto"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              disabled={isLoading}
            >
              Voltar
            </Button>
          </FixedFooter>
        </>
      )}
    </div>
  );
}
